import React from "react";

const SUMMARY_CONFIG = {
  fund: {
    description: "Your card will be funded with",
    labels: {
      card_masked_pan: "You're funding",
      card_event_amount: "You will pay",
      total_wallet_debit_amount: "Total wallet debit",
      card_event_fee: "Card fee",
      wallet_impact: "Paying with",
    },
    primaryAmountKeys: [
      "funding_amount",
      "card_event_amount",
      "total_wallet_debit_amount",
      "wallet_impact",
    ],
  },
  withdraw: {
    description: "You are withdrawing",
    labels: {
      card_masked_pan: "You're withdrawing from",
      card_event_amount: "You will receive",
      total_wallet_debit_amount: "Total wallet debit",
      card_event_fee: "Card fee",
      wallet_impact: "Receiving to",
    },
    primaryAmountKeys: [
      "withdraw_amount",
      "card_event_amount",
      "total_wallet_debit_amount",
      "wallet_impact",
    ],
  },
};

const labelFor = (key, labels = {}) => labels[key] || key
  .replace(/_/g, " ")
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const formatMaskedCardNumber = (value) => {
  const digits = String(value).replace(/\D/g, "");
  const lastFour = digits.slice(-4);

  return lastFour ? `**** **** **** ${lastFour}` : "**** **** ****";
};

const formatObjectValue = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value !== "object") return String(value);

  const currency = value.wallet_currency || value.currency || value.currency_code;
  const amount = value.wallet_amount ?? value.total_amount ?? value.amount;
  const rate = value.rate;
  if (currency && amount !== undefined) return `${currency} ${amount}`;
  if (currency && rate !== undefined) return `${currency} ${rate}`;
  if (value.description) return value.description;

  return null;
};

const HIDDEN_SUMMARY_KEYS = ["nickname", "card_event_fee", "card_brand", "card_name"];
const FLATTENED_SUMMARY_KEYS = ["card_event", "virtual_card"];

const getPrimaryAmount = (data, primaryAmountKeys) => {
  const key = primaryAmountKeys.find((amountKey) => data?.[amountKey] != null);
  if (!key) return null;

  const formatted = formatObjectValue(data[key]);
  return formatted === null ? null : { key, value: formatted };
};

const SummaryRows = ({
  data,
  path = "summary",
  hiddenKeys = [],
  hiddenGroupKeys = [],
  labels = {},
  operationType = "fund",
}) => {
  const entries = Object.entries(data || {});
  const deferredEntries = [];

  return entries.flatMap(([key, value]) => {
    if (operationType === "withdraw" && key === "card_event_amount") {
      deferredEntries.push([key, value]);
      return [];
    }

    if (value === null || value === undefined || value === "") return [];
    if (key === "card_event" && typeof value === "string") return [];
    if (HIDDEN_SUMMARY_KEYS.includes(key)) return [];
    if (hiddenKeys.includes(key)) return [];

    if (key === "card_masked_pan") {
      return (
        <div className="card-operation-summary-row" key={`${path}-${key}`}>
          <span>{labelFor(key, labels)}</span>
          <strong className="card-operation-summary-card">
            {data.nickname && <strong>{data.nickname}</strong>}
            <small>{formatMaskedCardNumber(value)}</small>
          </strong>
        </div>
      );
    }

    if (value?.display_title) {
      return (
        <div className="card-operation-summary-row" key={`${path}-${key}`}>
          <span>{labelFor(key, labels)}</span>
          <strong className="card-operation-summary-wallet">
            <strong>{value.display_title}</strong>
            {value.display_subtitle && <small>{value.display_subtitle}</small>}
          </strong>
        </div>
      );
    }

    const formatted = formatObjectValue(value);

    // Show these objects' fields without displaying an extra section heading.
    if (
      (FLATTENED_SUMMARY_KEYS.includes(key) || hiddenGroupKeys.includes(key))
      && formatted === null
      && !Array.isArray(value)
    ) {
      return (
        <SummaryRows
          key={`${path}-${key}`}
          data={value}
          path={`${path}-${key}`}
          hiddenKeys={hiddenKeys}
          hiddenGroupKeys={hiddenGroupKeys}
          labels={labels}
          operationType={operationType}
        />
      );
    }

    if (formatted === null && !Array.isArray(value)) {
      return (
        <React.Fragment key={`${path}-${key}`}>
          <div className="card-operation-summary-group">{labelFor(key, labels)}</div>
          <SummaryRows
            data={value}
            path={`${path}-${key}`}
            hiddenKeys={hiddenKeys}
            hiddenGroupKeys={hiddenGroupKeys}
            labels={labels}
            operationType={operationType}
          />
        </React.Fragment>
      );
    }

    return (
      <div className="card-operation-summary-row" key={`${path}-${key}`}>
        <span>{labelFor(key, labels)}</span>
        <strong>{Array.isArray(value) ? value.join(", ") : formatted}</strong>
      </div>
    );
  }).concat(
    deferredEntries.flatMap(([key, value]) => {
      if (value === null || value === undefined || value === "") return [];
      if (hiddenKeys.includes(key)) return [];

      const formatted = formatObjectValue(value);
      return (
        <div className="card-operation-summary-row" key={`${path}-${key}`}>
          <span>{labelFor(key, labels)}</span>
          <strong>{Array.isArray(value) ? value.join(", ") : formatted}</strong>
        </div>
      );
    })
  );
};

const CardOperationSummary = ({
  title,
  data,
  operationType = "fund",
  showHeader = true,
  primaryAmount: primaryAmountOverride,
  hiddenKeys = [],
  hiddenGroupKeys = [],
}) => {
  if (!data || !Object.keys(data).length) return null;

  const config = SUMMARY_CONFIG[operationType] || SUMMARY_CONFIG.fund;
  const detectedPrimaryAmount = showHeader ? getPrimaryAmount(data, config.primaryAmountKeys) : null;
  const primaryAmount = primaryAmountOverride && showHeader
    ? { ...detectedPrimaryAmount, ...primaryAmountOverride }
    : detectedPrimaryAmount;

  return (
    <div className="card-operation-summary">
      {showHeader && (
        <div className="card-operation-summary-header">
          <div className="card-operation-summary-icon" aria-hidden="true">
            <i className="fa-solid fa-receipt"></i>
          </div>
          <h6>{title}</h6>
          <p>{config.description}</p>
          {primaryAmount && (
            <div className="card-operation-summary-amount">
              <strong>{primaryAmount.value}</strong>
              {/* <span>{primaryAmount.label || labelFor(primaryAmount.key)}</span> */}
            </div>
          )}
        </div>
      )}

      <div className={`card-operation-summary-details${showHeader ? "" : " card-operation-summary-details--only"}`}>
        <SummaryRows
          data={data}
          hiddenKeys={[
            ...hiddenKeys,
            ...(primaryAmount?.key ? [primaryAmount.key] : []),
          ]}
          hiddenGroupKeys={hiddenGroupKeys}
          labels={config.labels}
          operationType={operationType}
        />
      </div>
    </div>
  );
};

export default CardOperationSummary;
